
import { Options, Vue } from "vue-class-component";
import StockService from "../../service/StockService.js";
import StockTransferService from "../../service/StockTransferService.js";
import Toaster from "../../helpers/Toaster";
import moment from "moment";
import { useStore } from "../../store";
import router from "../../router";
import AutoComplete from "primevue/autocomplete";
import BranchValidator from "../../utils/branchValidator.js";
import FooterService from "../../service/footerService.js";


interface BasketItem {
    stock_id: number;
    product_name: string;
    generic_name: string;
    batch_no: string;
    available_qty: number;
    qty: number;
    free_ratio: number; 
    paid_qty: number | null;   
    free_qty: number | null;
    purchase_price: number;
    mrp: number;
    expiry_date: string;
    qtyError: string;
    product_id: number;
    total_free_qty: number;
    total_paid_qty: number;
    stock_breakdown: any[];
}

@Options({
    title: "Create Stock Transfer",
    components: {AutoComplete},
})
export default class StockTransfer extends Vue {
    private branchId: any   = null;
    private branchName      = "";

    private store         = useStore();
    private stockService;
    private transferService;
    private toast;

    private toBranchId: number | null = null;
    private notes          = "";
    private searchKeyword: any  = "";
    private searchResults: any[] = [];
    private itemBasket: BasketItem[] = [];
    private branchList: any[]  = [];
    private submitted      = true;
    private lastSubmittedNo = "";
    private storeName      = "";

    private validationPopup        = false;
    private validationPopupMessage = "";

    private _channel: BroadcastChannel | null = null;
    private _unloadHandler: (e: BeforeUnloadEvent) => void = () => {};
    

    created() {
        this.stockService    = new StockService();
        this.transferService = new StockTransferService();
        this.toast           = new Toaster();
    }

    mounted() {
        this.loadBranches();
        if (typeof BroadcastChannel !== 'undefined') {
            this._channel = new BroadcastChannel('stock_transfer_channel');
        }
        window.addEventListener("keydown", this.handleKeydown);

        this._unloadHandler = (e: BeforeUnloadEvent) => {
            if (this.itemBasket.length > 0) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };
        window.addEventListener('beforeunload', this._unloadHandler);
        window.addEventListener("keypress", this.handleKeypress);

        this.$nextTick(() => {
            const el = document.getElementById('transfer-search-input');
            const input = el?.querySelector('input') as HTMLInputElement;
            input?.focus();
        });
    }

    beforeUnmount() {
        this._channel?.close();
        window.removeEventListener("keydown", this.handleKeydown);
        window.removeEventListener('beforeunload', this._unloadHandler);
        window.removeEventListener("keypress", this.handleKeypress); 
    }

    handleKeypress(e: KeyboardEvent) {
        if (this.validationPopup) return;

        // submit
        if (e.key === '*') {
            e.preventDefault();
            if (this.itemBasket.length > 0 && this.toBranchId && !this.hasAnyError) {
                this.submitTransfer();
            }
            return;
        }

        // focus search
        if (e.key === '+') {
            e.preventDefault();
            const el = document.getElementById('transfer-search-input');
            const input = el?.querySelector('input') as HTMLInputElement;
            input?.focus();
            return;
        }
    }

    handleKeydown(e: KeyboardEvent) {
        if (this.validationPopup) {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.validationPopup = false;
            }
            return;
        }

        // clear basket
        if (e.key === 'Delete') {
            e.preventDefault();
            if (this.itemBasket.length > 0) {
                if (window.confirm('Clear all items from the basket?')) {
                    this.clearBasket();
                }
            }
            return;
        }

        // focus search (clear autocomplete)
        if (e.key === 'Escape') {
            e.preventDefault();
            if (this.searchResults.length > 0) {
                this.searchResults = [];
                this.searchKeyword = '';
            }
            const el = document.getElementById('transfer-search-input');
            const input = el?.querySelector('input') as HTMLInputElement;
            input?.focus();
            return;
        }
    }

    loadBranches() {
        this.transferService.getBranchesForTransfer()
            .then((data: any) => {
                if (!data) {
                    this.toast.showError('Failed to load branches. Please refresh.');
                    return;
                }
                this.branchList   = data.branches ?? [];
                this.storeName    = data.current_branch_name ?? 'Transfer';
                this.branchId     = data.current_branch_id ?? null;
                this.branchName   = data.current_branch_name ?? '';
            })
            .catch((e: any) => {
                console.error('Failed to load branches', e);
                this.toast.showError('Failed to load branches. Check server logs.');
            });
    }

    searchStock(event: any) {
        const query = typeof event === 'string' ? event : event?.query ?? '';
        if (!query.trim()) return;

        this.stockService.searchStockForTransfer(query).then((res: any) => {
            if (!res || !res.records) {
                this.searchResults = [];
                return;
            }

            if (res.records.length === 0) {
                this.toast.showInfo("No Item Found");
                this.searchResults = [];
                return;
            }

            if (res.alert === 'frozen') {
                this.validationPopupMessage = res.msg;
                this.validationPopup = true;
                this.searchResults = [];
                return;
            }

            if (this.itemBasket.length > 0) {
                const storedBranch = BranchValidator.getStoredTransactionBranch();
                if (storedBranch && this.branchId && parseInt(this.branchId) !== parseInt(storedBranch.branchId)) {
                    this.toast.showError(
                        `Branch mismatch! Transfer started in "${storedBranch.branchName}" but you are currently in "${this.branchName}". Please switch back or clear the basket.`
                    );
                    this.searchResults = [];
                    return;
                }
            }

            const inBasket = new Set(this.itemBasket.map(i => i.stock_id));
            const unavailable = res.records.filter(
                (r: any) => !inBasket.has(r.id) && r.is_unavailable
            );
            const alreadyInBasket = res.records.filter(
                (r: any) => inBasket.has(r.id)
            );
            this.searchResults = res.records.filter(
                (r: any) => !inBasket.has(r.id) && !r.is_unavailable
            );

            if (this.searchResults.length === 0) {
                // Prefer the "already added" message — a partially returned
                // batch also emits an unavailable "Returned" row, which would
                // otherwise shadow the fact that its active part is in the basket.
                if (alreadyInBasket.length > 0) {
                    const first = alreadyInBasket[0];
                    this.toast.showWarning(
                        `"${first.product_name}" (Batch: ${first.batch_no}) is already added to the transfer.`
                    );
                } else if (unavailable.length > 0) {
                    const first = unavailable[0];
                    this.toast.showError(
                        `"${first.product_name}" (Batch: ${first.batch_no}) is unavailable and cannot be transferred.`
                    );
                }
            }
        }).catch(() => {
            this.toast.showError("Search failed. Please try again.");
        });
    }

    addFromAutoComplete(event: any) {
        const stock = event.value;

        this.searchKeyword = "";
        this.searchResults = [];

        this.$nextTick(() => {
            const el = document.getElementById('transfer-search-input');
            const input = el?.querySelector('input') as HTMLInputElement;
            input?.focus();
        });

        if (!stock || !stock.id) return;

        if (stock.is_unavailable) {
            this.toast.showError(
                `"${stock.product_name}" (Batch: ${stock.batch_no}) is unavailable and cannot be transferred.`
            );
            return;
        }

        this.addToBasket(stock);
    }

    addToBasket(stock: any) {
        if (this.itemBasket.find(i => i.stock_id === stock.id)) {
            this.toast.showWarning("This batch is already in the basket.");
            return;
        }
        this.transferService.checkFreeze(stock.id).then((res: any) => {
            if (res.frozen) {
                this.validationPopupMessage = res.msg;
                this.validationPopup = true;
                return;
            }

            if (this.itemBasket.length === 0) {
                BranchValidator.storeTransactionBranch(
                    this.branchId,
                    this.branchName,
                    'TRN',
                    ''
                );
            }
            this.itemBasket.push({
                stock_id:        stock.id,
                product_name:    stock.product_name,
                product_id:      stock.product_id,
                generic_name:    stock.generic_name ?? stock.generic ?? '',
                batch_no:        stock.batch_no,
                available_qty:   Number(stock.qty),
                qty:             0,
                free_ratio:      Number(stock.free_ratio ?? 0),
                total_free_qty:  Number(stock.total_free_qty ?? 0),
                total_paid_qty:  Number(stock.total_paid_qty ?? 0),
                paid_qty:        null,   
                free_qty:        null,
                purchase_price:  Number(stock.purchase_price),
                mrp:             Number(stock.mrp),
                expiry_date:     stock.expiry_date,
                stock_breakdown: stock.stock_breakdown ?? [],
                qtyError:        'Transfer qty is required.',
            });
            this.searchResults = this.searchResults.filter(r => r.id !== stock.id);
        });
    }

    removeFromBasket(stockId: number) {
        const item = this.itemBasket.find(i => i.stock_id === stockId);
        if (!item) return;
        
        if (!window.confirm(`Remove "${item.product_name}" (${item.batch_no}) from basket?`)) {
            return;
        }
        
        this.itemBasket = this.itemBasket.filter(i => i.stock_id !== stockId);
        if (this.itemBasket.length === 0) {
            BranchValidator.clearStoredTransactionBranch();
        }
    }

    clearBasket() {
        this.itemBasket      = [];
        this.searchResults   = [];
        this.searchKeyword   = "";
        this.lastSubmittedNo = "";
        this.toBranchId      = null;
        this.notes           = "";
        BranchValidator.clearStoredTransactionBranch();
    }

    validateBasketItem(item: BasketItem) {
        if (!item.qty || item.qty <= 0) {
            item.qtyError = "Transfer qty is required.";
        } else if (item.qty > item.available_qty) {
            item.qtyError = `Max available is ${item.available_qty}.`;
        } else {
            item.qtyError = "";
        }
    }

    get basketTotalCost(): number {
        return this.itemBasket.reduce(
            (sum, i) => sum + (i.paid_qty ?? (i.qty || 0)) * i.purchase_price, 0
        );
    }

    get selectedBranchName(): string {
        const b = this.branchList.find((b: any) => b.id === this.toBranchId);
        return b ? `${b.name} (${b.code})` : '';
    }

    submitTransfer() {
        const branchValidation = BranchValidator.validateCurrentBranch(this.branchId, this.branchName);
        if (this.itemBasket.length > 0 && !branchValidation.isValid) {
            this.validationPopupMessage = branchValidation.message;
            this.validationPopup = true;
            return;
        }

        if (!this.toBranchId) {
            this.validationPopupMessage = "Please select a destination branch.";
            this.validationPopup = true;
            return;
        }
        if (this.itemBasket.length === 0) {
            this.validationPopupMessage = "Add at least one item to the basket.";
            this.validationPopup = true;
            return;
        }

        let hasError = false;
        this.itemBasket.forEach(item => {
            this.validateBasketItem(item);
            if (item.qtyError) hasError = true;
        });
        if (hasError) {
            this.validationPopupMessage = "Please fix qty errors before submitting. All items must have a valid transfer quantity.";
            this.validationPopup = true;
            return;
        }

        const freezeChecks = this.itemBasket.map(item =>
            this.transferService.checkFreeze(item.stock_id).then((res: any) => ({
                item,
                frozen: res.frozen,
                msg:    res.msg ?? '',
            }))
        );

        Promise.all(freezeChecks).then((results: any[]) => {
            const frozenItems = results.filter(r => r.frozen);
            if (frozenItems.length > 0) {
                const details = frozenItems
                    .map(r => `${r.item.product_name} (${r.item.batch_no}): ${r.msg}`)
                    .join('\n');
                this.validationPopupMessage = `Cannot submit - the following items are being edited:\n\n${details}`;
                this.validationPopup = true;
                return;
            }

            const payload = this.itemBasket.map(i => ({
                stock_id:   i.stock_id,
                product_id: i.product_id,
                batch_no:   i.batch_no,   
                qty:        i.qty,
            }));

            this.transferService
                .initiateTransfer(this.toBranchId, payload, this.notes)
                .then((res: any) => {
                    if (res.alert === 'frozen' || res.alert === 'danger') {
                        this.validationPopupMessage = res.msg;
                        this.validationPopup = true;
                        return;
                    }
                    this.toast.handleResponse(res);
                    if (res.alert === 'info') {
                        this.lastSubmittedNo = res.transfer_no;
                        this._channel?.postMessage({
                            type:        'transfer_created',
                            transfer_no: res.transfer_no,
                        });
                        this.itemBasket    = [];
                        this.searchResults = [];
                        this.searchKeyword = "";
                        this.notes         = "";
                        this.toBranchId    = null;   
                        BranchValidator.clearStoredTransactionBranch();
                    }
                });
        });
    }

    redirectHome() {
        router.replace({ path: "/home/dashboard" });
    }

    get progressBar() {
        return this.store.getters.getProgressBar;
    }

    fixDigits(amt: number) {
        return Number(amt || 0).toFixed(2);
    }

    formatExpiry(date: string) {
        return date ? moment(date).format("DD-MM-YYYY") : "—";
    }

    get currency() {
        return this.store.getters.getCurrency;
    }

    onQtyChange(item: BasketItem, val: number | null) {
        if (val === null || val === undefined || val <= 0) {
            item.qty      = val ?? 0;
            item.paid_qty = null;  
            item.free_qty = null;   
            item.qtyError = "Transfer qty is required.";
            return;
        }
        
        if (val > item.available_qty) {
            this.toast.showError(
                `Only ${item.available_qty} units available for ${item.product_name} (${item.batch_no}).`
            );
            item.qtyError = `Max available is ${item.available_qty}.`;
            this.$nextTick(() => {
                item.qty = item.available_qty;
                const split = this._splitTransferQty(item.stock_breakdown, item.available_qty);
                item.free_qty = split.free;
                item.paid_qty = split.paid;
            });
            return;
        }

        item.qty      = val;
        item.qtyError = "";

        const split = this._splitTransferQty(item.stock_breakdown, val);
        item.free_qty = split.free;
        item.paid_qty = split.paid;
    }

    private _splitTransferQty(breakdown: any[], qty: number): { paid: number; free: number } {
        let remaining = qty;
        let paid = 0;
        let free = 0;
        for (const s of breakdown) {
            if (remaining <= 0) break;
            const take = Math.min(remaining, s.qty);
            const freeTake = Math.min(take, s.free_qty);
            const paidTake = take - freeTake;
            free += freeTake;
            paid += paidTake;
            remaining -= take;
        }
        return { paid, free };
    }

    get hasAnyError(): boolean {
        return this.itemBasket.some(i => !!i.qtyError);
    }

    adjustQty(type: 'add' | 'minus', item: BasketItem) {
        if (type === 'add') {
            const newVal = (item.qty || 0) + 1;
            if (newVal > item.available_qty) {
                this.toast.showError(
                    `Only ${item.available_qty} units available for ${item.product_name} (${item.batch_no}).`
                );
                return;
            }
            this.onQtyChange(item, newVal);
        } else {
            const newVal = (item.qty || 0) - 1;
            if (newVal <= 0) {
                item.qty      = 0;
                item.paid_qty = null;
                item.free_qty = null;
                item.qtyError = 'Quantity must be at least 1.';
                return;
            }
            this.onQtyChange(item, newVal);
        }
    }

    onQtyInput(item: BasketItem, event: Event) {
        const input = event.target as HTMLInputElement;
        let sanitized = input.value.replace(/[^0-9]/g, '');
        if (sanitized === '') {
            input.value = '';
            item.qty      = 0;
            item.paid_qty = null;
            item.free_qty = null;
            item.qtyError = 'Transfer qty is required.';
            return;
        }
        const val = parseInt(sanitized, 10);
        input.value = sanitized;
        this.onQtyChange(item, val);
    }
}
